import { supabase } from '@/integrations/supabase/client';

export interface ExerciseInfo {
  id: string;
  name: string;
  gifUrl: string;
  bodyPart: string;
  target: string;
  equipment: string;
  secondaryMuscles: string[];
  instructions: string[];
}

export interface ExerciseLookupResponse {
  success: boolean;
  error?: string;
  exercise?: ExerciseInfo;
  alternatives?: ExerciseInfo[];
}

// Cache to avoid repeated API calls
const exerciseCache = new Map<string, ExerciseLookupResponse>();

export async function lookupExercise(exerciseName: string): Promise<ExerciseLookupResponse> {
  const cacheKey = exerciseName.toLowerCase().trim();
  
  // Check cache first
  if (exerciseCache.has(cacheKey)) {
    return exerciseCache.get(cacheKey)!;
  }

  try {
    const { data, error } = await supabase.functions.invoke('exercise-lookup', {
      body: { exerciseName },
    });

    // Handle Supabase invoke error
    if (error) {
      console.warn('Exercise lookup error:', error);
      return { success: false, error: error.message || 'Failed to lookup exercise' };
    }

    // Handle edge function error responses (404s, etc.)
    if (data && !data.success) {
      return { success: false, error: data.error || 'Exercise not found' };
    }

    // Cache successful results
    if (data?.success) {
      exerciseCache.set(cacheKey, data);
    }

    return data || { success: false, error: 'No data returned' };
  } catch (error) {
    console.error('Error looking up exercise:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to lookup exercise' 
    };
  }
}
